import { useTheme } from 'next-themes';
import { Laptop, Moon, Sun } from 'lucide-react';

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Button } from '@/components/ui/button';

/**
 * Three-option theme toggle: light / dark / system.
 *
 * Renders a single icon button whose icon reflects the active theme
 * (Sun = light, Moon = dark, Laptop = system / indeterminate).  Clicking
 * opens a DropdownMenu with the three choices.  The selected theme is
 * persisted to `localStorage` automatically by next-themes.
 *
 * Intended mounting point: inside `AgentIdentityFooter` in AccountSidebar,
 * on the same row as the agent identity button, to the left of it.
 */
export function ThemeToggle(): React.JSX.Element {
  const { theme, setTheme } = useTheme();

  // Derive which icon to show in the trigger.
  const Icon =
    theme === 'dark' ? Moon : theme === 'light' ? Sun : Laptop;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="size-8 shrink-0 text-muted-foreground hover:text-foreground"
          aria-label="Toggle theme"
        >
          <Icon className="size-4" aria-hidden="true" />
        </Button>
      </DropdownMenuTrigger>

      <DropdownMenuContent align="start" side="top" className="min-w-[8rem]">
        <DropdownMenuItem onSelect={() => setTheme('light')}>
          <Sun className="mr-2 size-4" aria-hidden="true" />
          Light
        </DropdownMenuItem>
        <DropdownMenuItem onSelect={() => setTheme('dark')}>
          <Moon className="mr-2 size-4" aria-hidden="true" />
          Dark
        </DropdownMenuItem>
        <DropdownMenuItem onSelect={() => setTheme('system')}>
          <Laptop className="mr-2 size-4" aria-hidden="true" />
          System
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
